import { useState } from 'react';
import type { ClientLifecycleEvent, TelemetryScoreClientResponse } from '@siliconx/shared';
import type { ClientFmvSnapshot } from '../../asset-detail/asset-detail-types';
import { conditionLabel } from '@siliconx/shared/ui/ConditionBadge';
import { LifecycleBadge, lifecycleLabel } from '@siliconx/shared/ui/LifecycleBadge';
import { formatDate } from '../../../lib/format';
import {
  Card,
  CardLabel,
  MiniTable,
  TableToggleFooter,
  downloadCsv,
  assetIsListable,
  lifecycleEventLabel,
  locationOrTeamEventLabel,
  type LifeEvent,
} from '../shared';

/**
 * SIL-565: Lifecycle tab — event timeline, lifecycle management, and list-
 * for-sale. Split out of AssetDetailV2.tsx; content and event-derivation
 * logic unchanged from the prior inline render (RUF-280/RUF-308/RUF-337/
 * RUF-448/RUF-380 origin).
 */

function LifecycleCard({
  events,
  nextStep,
  createdAt,
  updatedAt,
  lifecycleStateId,
  lifecycleStateName,
}: {
  events: LifeEvent[];
  nextStep: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  lifecycleStateId: string | null;
  lifecycleStateName: string | null;
}) {
  const [open, setOpen] = useState(false);
  const total = events.length + (nextStep ? 1 : 0);
  // Nodes spread 4%..96%, labels alternating above/below the line like the mock.
  const pos = (i: number) => (total <= 1 ? 4 : 4 + (i * 92) / (total - 1));
  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardLabel>Asset lifecycle</CardLabel>
        {lifecycleStateId && (
          <LifecycleBadge
            id={lifecycleStateId}
            label={lifecycleStateName ?? lifecycleLabel(lifecycleStateId)}
          />
        )}
      </div>
      <div className="relative mt-1 h-[130px]">
        <div className="absolute left-0 right-0 top-[63px] h-0.5 bg-zinc-200" />
        {events.length > 1 && (
          <div
            className="absolute top-[63px] h-0.5 bg-brand-500"
            style={{ left: `${pos(0)}%`, width: `${pos(events.length - 1) - pos(0)}%` }}
          />
        )}
        {events.map((e, i) => (
          <div key={e.id ?? e.label}>
            <div
              className="absolute top-[64px] size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"
              style={{ left: `${pos(i)}%` }}
            />
            <div
              className={`absolute -translate-x-1/2 whitespace-nowrap text-center text-xs font-semibold text-zinc-800 ${
                i % 2 === 0 ? 'top-[22px]' : 'top-[86px]'
              }`}
              style={{ left: `${Math.min(Math.max(pos(i), 8), 92)}%` }}
            >
              {e.label}
              <div className="font-mono text-[13px] font-normal text-zinc-500">
                {formatDate(e.date)}
              </div>
            </div>
          </div>
        ))}
        {nextStep && (
          <>
            <div
              className="absolute top-[64px] size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-dashed border-zinc-400 bg-white"
              style={{ left: `${pos(total - 1)}%` }}
            />
            <div
              className={`absolute -translate-x-1/2 whitespace-nowrap text-center text-xs font-semibold text-zinc-500 ${
                (total - 1) % 2 === 0 ? 'top-[22px]' : 'top-[86px]'
              }`}
              style={{ left: `${Math.min(pos(total - 1), 92)}%` }}
            >
              {nextStep}
              <div className="text-xs font-normal">Next step</div>
            </div>
          </>
        )}
      </div>
      <div className="text-xs text-zinc-500">
        Every event is recorded to the Silicon Passport — the chain is the evidence.
        {createdAt && ` Created ${formatDate(createdAt)}`}
        {updatedAt && ` · Updated ${formatDate(updatedAt)}`}.
      </div>
      <TableToggleFooter
        open={open}
        onToggle={() => setOpen(!open)}
        onExport={() =>
          downloadCsv(
            'asset-lifecycle.csv',
            ['Event', 'Date', 'Record'],
            events.map((e) => [e.label, formatDate(e.date), e.record]),
          )
        }
      />
      {open && (
        <MiniTable
          header={['Event', 'Date', 'Record']}
          rows={events.map((e) => [e.label, formatDate(e.date), `# ${e.record}`])}
        />
      )}
    </Card>
  );
}

export interface LifecycleTabProps {
  asset: {
    id: string;
    condition: string | null;
    lifecycleAllowsListing: boolean;
    lifecycleStateId: string | null;
    lifecycleStateName: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    latestFmv: { pricedAt: string } | null;
  };
  snapshotHistory: ClientFmvSnapshot[];
  lifecycleEvents: ClientLifecycleEvent[];
  score: TelemetryScoreClientResponse | null;
  provenance?: React.ReactNode;
  lifecycleManagement?: React.ReactNode;
  listForSale?: React.ReactNode;
}

export function LifecycleTab({
  asset,
  snapshotHistory,
  lifecycleEvents,
  score,
  provenance,
  lifecycleManagement,
  listForSale,
}: LifecycleTabProps) {
  const events: LifeEvent[] = [];
  if (asset.createdAt) {
    events.push({ label: 'Registered', date: asset.createdAt, record: asset.id.slice(0, 8) });
  }
  if (score) {
    events.push({
      label: 'Telemetry scored',
      date: score.scoredAt,
      record: `score ${Math.round(score.overall)}`,
    });
    if (asset.condition) {
      events.push({
        label: `Graded ${conditionLabel(asset.condition)}`,
        date: score.scoredAt,
        record: asset.condition,
      });
    }
  }
  if (asset.latestFmv) {
    events.push({
      label: 'FMV priced',
      date: asset.latestFmv.pricedAt,
      record: snapshotHistory[0]?.snapshotId.slice(0, 8) ?? 'snapshot',
    });
  }
  // Lifecycle timeline events: stage transitions (active/idle/decommissioned)
  // plus location/team change events (migration 678, RUF-448/RUF-380). Keyed
  // on eventId so repeated values don't collide. Owner-facing only — the
  // buyer/customer passport never renders any of these.
  for (const ev of lifecycleEvents) {
    if (ev.eventType === 'lifecycle_state' && ev.fromState && ev.toState) {
      events.push({
        id: ev.eventId,
        label: lifecycleEventLabel(ev.toState),
        date: ev.createdAt,
        record: `${ev.fromState} → ${ev.toState}`,
      });
    } else if (ev.eventType === 'location_change' || ev.eventType === 'team_change') {
      const { label, record } = locationOrTeamEventLabel(ev);
      events.push({ id: ev.eventId, label, date: ev.createdAt, record });
    }
  }
  events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return (
    <>
      <LifecycleCard
        events={events}
        nextStep={assetIsListable(asset) ? 'List for sale' : null}
        createdAt={asset.createdAt}
        updatedAt={asset.updatedAt}
        lifecycleStateId={asset.lifecycleStateId}
        lifecycleStateName={asset.lifecycleStateName}
      />
      {provenance}
      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
        {lifecycleManagement}
        {listForSale}
      </div>
    </>
  );
}
